/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import { GoogleAnalytics } from 'scripts/social/ga';
import domUtils from 'scripts/utils/dom';
import deviceUtil from 'scripts/utils/device';
import can from 'can';
import appContext from './app-context';
import Page from './page';
import 'scripts/config-cloudinary';
import history from 'scripts/utils/history';
import 'scripts/utils/autorender';
import svg4everybody from 'svg4everybody';
import 'scripts/utils/registerStacheHelpers';

/* COMMON MODULES AND COMPONENTS */
import 'blocks/navigation/hamburger-menu/hamburger-menu';
import 'blocks/navigation/header/header';
import 'components/image-generic/image-generic';
import 'components/image-cloudinary/image-cloudinary';
import 'components/svg-image/svg-image';
import 'blocks/partner-link/partner-link';

let $window = can.$(window);
let $document = can.$(document);
let $html = can.$('html');

const DOCUMENT_RESIZE_INTERVAL = 200;

let onWindowResize = () => {
    let viewportSize = domUtils.getViewportSize();
    let width = _.get(viewportSize, 'width', 0);
    let height = _.get(viewportSize, 'height', 0);
    let orientation = width > height ? 'landscape' : 'portrait';

    appContext.model.screen.attr('screenDimensions', {
        width: width,
        height: height
    });

    appContext.model.screen.attr('screenOrientation', orientation);
};

let onDocumentResize = (width, height) => {
    appContext.model.screen.attr('documentDimensions', {
        width: width,
        height: height
    });
};

let onWindowScroll = () => {
    let scrollTop = window.pageYOffset;
    let previousOffset = appContext.model.screen.attr('scrollOffset') === undefined ? window.pageYOffset : appContext.model.screen.attr('scrollOffset');

    appContext.model.screen.attr('scrollDirection', previousOffset <= scrollTop ? 'down' : 'up');
    appContext.model.screen.attr('scrollOffset', scrollTop);

};

let onWindowLoad = () => {
    appContext.model.screen.attr('windowLoad', true);
};

let watchWindowLoad = (callback) => {
    if ($document.attr('readyState') === 'complete') {
        callback();
    }
    $window.on('load', callback);
};

let onDocumentReady = () => {
    appContext.model.screen.attr('documentReady', true);
    onWindowResize();
    onWindowScroll();
    $window.on('resize', onWindowResize);
    $window.on('scroll', onWindowScroll);
    domUtils.watchElementSize($document, onDocumentResize, DOCUMENT_RESIZE_INTERVAL);
};

let checkDevice = () => {
    if (deviceUtil.isMobile) {
        $html.addClass('mobile');
    }
    if (deviceUtil.isTouch) {
        $html.addClass('touch');
    } else {
        $html.addClass('no-touch');
    }
    if (deviceUtil.isNative) {
        $html.addClass('native');
    }
};

let configureGoogleAnalytics = () => {
    GoogleAnalytics.initialize({
        /* any trackPage() call will be postponed until flag is changed */
        delayTracking: false,

        // enable link click tracking
        trackLinkClick: false,

        // className of the tracking element
        trackingLinkClass: 'ga-tracking-link',

        // how deep we check DOM for parents of the target click element
        trackingLinkLevel: 5,

        // indexes should be configured in the GA admin panel
        gaFieldsMap: {
            page_type: 'dimension1',
            content_tag: 'dimension2',
            is_native: 'dimension3'
        }
    });
};

history.filterHashPagesInHistory(window);
svg4everybody();
checkDevice();
configureGoogleAnalytics();

new Page('html');
$document.ready(onDocumentReady);
watchWindowLoad(onWindowLoad);
